/**
 * imageFallback.js — recover from an expired presigned image URL.
 *
 * R2 view URLs expire after an hour. A page left open longer than that (the
 * chat layout routinely is) ends up holding dead URLs, and the browser
 * reports that the same way as any other load failure: an `error` event.
 *
 * So on error we do NOT immediately show the placeholder. We ask the server
 * for a fresh signed URL using the durable key, and only fall back to the
 * placeholder if that also fails. Under USE_R2=false there is no key on the
 * payload, so this degrades to exactly the old behaviour.
 *
 * Contract with the API: list and detail endpoints return both a renderable
 * URL and its key (`bookCoverKey`, `coverImageKey`, `profilePictureKey`).
 * The key is what survives; the URL is disposable.
 */

import axios from './axios';
import { API_ENDPOINTS } from '../config';

/** Inline SVG shown when an image genuinely cannot be loaded. */
export const BOOK_COVER_PLACEHOLDER =
  "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22400%22%20height%3D%22600%22%20viewBox%3D%220%200%20400%20600%22%3E%3Crect%20fill%3D%22%233B82F6%22%20width%3D%22400%22%20height%3D%22600%22%2F%3E%3Ctext%20fill%3D%22%23FFFFFF%22%20font-family%3D%22Arial%2C%20sans-serif%22%20font-size%3D%2224%22%20text-anchor%3D%22middle%22%20x%3D%22200%22%20y%3D%22300%22%3E%3Ctspan%20x%3D%22200%22%20dy%3D%220%22%3EBook%20Cover%3C%2Ftspan%3E%3Ctspan%20x%3D%22200%22%20dy%3D%2230%22%3ENot%20Available%3C%2Ftspan%3E%3C%2Ftext%3E%3C%2Fsvg%3E";

/**
 * In-flight and recently-failed keys, so a grid of 20 covers sharing one
 * expired key issues one refresh request rather than 20.
 */
const inFlight = new Map();

/**
 * Ask the server to re-sign a key.
 * @returns {Promise<string|null>} fresh URL, or null if it cannot be refreshed
 */
export async function refreshMediaUrl(key) {
  if (!key) return null;
  if (inFlight.has(key)) return inFlight.get(key);

  const request = axios
    .post(API_ENDPOINTS.REFRESH_MEDIA_URL, { key })
    .then((res) => res?.data?.url || null)
    .catch(() => null)
    .finally(() => {
      // Let a later expiry try again, but not within the same render pass.
      setTimeout(() => inFlight.delete(key), 5000);
    });

  inFlight.set(key, request);
  return request;
}

/**
 * Build an <img onError> handler that tries a re-sign before giving up.
 *
 * @param {string|null} key          durable object key, if the API sent one
 * @param {string} placeholder       data URI to show when all else fails
 * @returns {(event) => void}
 */
export function makeImageErrorHandler(key, placeholder = BOOK_COVER_PLACEHOLDER) {
  return async (event) => {
    const img = event.target;

    // Guard against a loop: the refreshed URL failing must not re-enter.
    if (img.dataset.refreshAttempted === 'true') {
      img.onerror = null;
      img.src = placeholder;
      return;
    }
    img.dataset.refreshAttempted = 'true';

    if (!key) {
      img.onerror = null;
      img.src = placeholder;
      return;
    }

    const fresh = await refreshMediaUrl(key);
    if (fresh) {
      img.src = fresh;
      return;
    }

    img.onerror = null;
    img.src = placeholder;
  };
}
